/**
 * `@navio-maritime/platform/observability` — shared types + the canonical
 * value-loop / funnel event contract.
 *
 * This is BROWSER-SAFE. No server-only imports (no `jose`, node crypto, fs,
 * the auth/verify code). Safe to bundle in a Vite frontend.
 */
/**
 * Canonical value-loop / funnel events shared across the Navio ecosystem.
 *
 * Grounded in the NavioFit funnel defined in
 * `001 - NavioWorks/Business/05-Operations/naviofit-monitoring-slos.md` §6.
 * Each product reuses the canonical names where the step maps cleanly, and may
 * additionally emit product-specific string events (see {@link ObservabilityEventName}).
 *
 * NOTE: these are the PRODUCT-AGNOSTIC funnel-step names. The `product`
 * super-property (set at init) keeps them attributable per product, so we do
 * not bake the product prefix into the constant. A consumer that wants the
 * literal NavioFit-prefixed name (`naviofit_logged_in`) may still pass that as
 * a product-specific string.
 */
export const FUNNEL_EVENTS = {
    /** Authenticated / logged in. */
    signedIn: 'user_signed_in',
    /** Signed up / first account creation. */
    signedUp: 'user_signed_up',
    /** Signed out. */
    signedOut: 'user_signed_out',
    /** Top-level container created (refit/new-build project, fleet, etc.). */
    projectCreated: 'project_created',
    /** A work item / WBS node / task created. */
    itemCreated: 'item_created',
    /** A scope-of-work (or equivalent scoping artifact) created. */
    scopeCreated: 'scope_created',
    /** A scope/request sent outward (e.g. crew → contractor). */
    scopeSent: 'scope_sent',
    /** A file uploaded (evidence, quote, document, photo). */
    fileUploaded: 'file_uploaded',
    /** An inbound quote uploaded (e.g. contractor portal). */
    quoteUploaded: 'quote_uploaded',
    /** A handover / cross-product push completed. */
    handoverCompleted: 'handover_completed',
};
